/**
 * アスリートメタオブジェクト JSON の全ページ読み込み
 *
 * snippets/athletes-data-script.liquid が paginate（250件/ページ）で出力した
 * application/json を起点に、2ページ目以降を Section Rendering API で取得して結合する。
 *
 * 読み込み順（必須）:
 *   1. snippets/athletes-loader-bootstrap.liquid … 同期スタブ + 本ファイルを defer
 *   2. セクションのインラインJS … enhanceWhenReady を呼ぶ（スタブならキューへ）
 *   3. 本ファイル実行 … 実処理を登録しキューをフラッシュ
 *
 * 使い方:
 *   window.SalomonAthletes.enhanceWhenReady(dataNode, (merged) => {
 *     allAthletes = merged;
 *     reloadItems();
 *   });
 *
 * 注意: bootstrap は各アスリートセクションから render されるため、同一ページで
 * 本スクリプトが複数回実行され得る。IIFE + _loaderReady ガードで冪等にしている。
 */
(() => {
  window.SalomonAthletes = window.SalomonAthletes || {};
  window.SalomonAthletes._enhanceQueue = window.SalomonAthletes._enhanceQueue || [];
  if (window.SalomonAthletes._loaderReady) return;

  /**
   * dataNode（1ページ目 JSON）から全ページ分を結合して返す。
   * 失敗ページはスキップし、最終件数が data-athletes-total と不一致なら console.warn する。
   */
  window.SalomonAthletes.loadAllFromJsonNode = async function loadAllFromJsonNode(dataNode) {
    if (!dataNode) return [];

    let athletes = [];
    try {
      athletes = JSON.parse(dataNode.textContent || "[]");
    } catch (e) {
      console.warn("[SalomonAthletes] 1ページ目 JSON のパースに失敗しました", e);
      athletes = [];
    }
    if (!Array.isArray(athletes)) athletes = [];

    const expectedTotal = parseInt(dataNode.dataset.athletesTotal || "0", 10);
    const totalPages = parseInt(dataNode.dataset.athletesPages || "1", 10);
    const sectionId = dataNode.dataset.sectionId || "";
    if (totalPages <= 1 || !sectionId) return athletes;

    const basePath = window.location.pathname;
    const failedPages = [];

    for (let page = 2; page <= totalPages; page++) {
      try {
        const params = new URLSearchParams({
          sections: sectionId,
          page: String(page)
        });
        const res = await fetch(`${basePath}?${params.toString()}`, {
          credentials: "same-origin"
        });
        if (!res.ok) {
          failedPages.push({ page, reason: `HTTP ${res.status}` });
          continue;
        }

        const json = await res.json();
        const html = json[sectionId] || json[`sections/${sectionId}`];
        if (!html) {
          failedPages.push({
            page,
            reason: `レスポンスに section キーなし (tried: ${sectionId})`,
            keys: Object.keys(json)
          });
          continue;
        }

        const doc = new DOMParser().parseFromString(html, "text/html");
        const pageNode = doc.getElementById(dataNode.id);
        if (!pageNode?.textContent) {
          failedPages.push({ page, reason: `script#${dataNode.id} が見つからない` });
          continue;
        }

        const chunk = JSON.parse(pageNode.textContent);
        if (Array.isArray(chunk)) {
          athletes = athletes.concat(chunk);
        } else {
          failedPages.push({ page, reason: "JSON が配列ではない" });
        }
      } catch (e) {
        failedPages.push({ page, reason: e?.message || String(e) });
      }
    }

    if (failedPages.length > 0) {
      console.warn(
        "[SalomonAthletes] 追加ページの取得に失敗しました。先頭ページ分のみ表示されます。",
        { sectionId, totalPages, expectedTotal, loaded: athletes.length, failedPages }
      );
    }

    // disciplines 絞り込み済み JSON は総数と一致しないため件数チェックをスキップ
    const isFiltered = dataNode.dataset.athletesFiltered === "true";
    if (!isFiltered && expectedTotal > 0 && athletes.length !== expectedTotal) {
      console.warn(
        "[SalomonAthletes] 結合後の件数と data-athletes-total が一致しません。",
        { expectedTotal, loaded: athletes.length, totalPages, sectionId, failedPages }
      );
    }

    return athletes;
  };

  /**
   * 251件超のときだけ残りページを取得し、件数が増えたときコールバックする。
   *
   * @param {HTMLElement|null} dataNode
   * @param {(merged: Array) => void} onMerged
   */
  window.SalomonAthletes.enhanceWhenReady = function enhanceWhenReady(dataNode, onMerged) {
    if (!dataNode || typeof onMerged !== "function") return;

    const totalPages = parseInt(dataNode.dataset.athletesPages || "1", 10);
    if (totalPages <= 1) return;

    (async () => {
      const firstPageCount = (() => {
        try {
          const parsed = JSON.parse(dataNode.textContent || "[]");
          return Array.isArray(parsed) ? parsed.length : 0;
        } catch (e) {
          return 0;
        }
      })();

      const merged = await window.SalomonAthletes.loadAllFromJsonNode(dataNode);
      if (Array.isArray(merged) && merged.length > firstPageCount) {
        onMerged(merged);
      }
    })();
  };

  window.SalomonAthletes._loaderReady = true;

  window.SalomonAthletes._enhanceQueue.splice(0).forEach(([dataNode, onMerged]) => {
    window.SalomonAthletes.enhanceWhenReady(dataNode, onMerged);
  });
})();
